import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';

gsap.registerPlugin(ScrollTrigger, SplitText);

if ('scrollRestoration' in history) {
  history.scrollRestoration = 'manual';
}

export function initScrollAnimations() {
  ScrollTrigger.config({ ignoreMobileResize: true });

  window.addEventListener('orientationchange', () => {
    setTimeout(() => ScrollTrigger.refresh(), 250);
  });

  const TILE_FADE_UP_DISTANCE = 80;
  const DESIGN_HEIGHT = 1080;
  const FADE_END_FACTOR = 0.6;

  const scrollDistance = (designPx) => window.innerHeight * (designPx / DESIGN_HEIGHT);

  const fadeUpTiles = gsap.utils.toArray([
    '.pose',
    '.pose-2',
    '.pose-3',
    '.pose-4',
    '.pose-5',
    '.pose-6',
  ].join(','));

  function applyFadeUp(el) {
    if (!el) {
      return;
    }

    gsap.from(el, {
      y: TILE_FADE_UP_DISTANCE,
      autoAlpha: 0,
      duration: 0.9,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: el,
        start: 'top 180%',
        once: true,
      },
    });
  }

  fadeUpTiles.forEach(applyFadeUp);

  function applyTubeReveal(title, trigger) {
    if (!title) {
      return;
    }

    const split = new SplitText(title, {
      type: 'words,chars',
      charsClass: 'tube-char',
      wordsClass: 'tube-word',
    });

    const fontSize = parseFloat(window.getComputedStyle(title).fontSize);
    const depth = Math.min(fontSize * 0.4, 50);
    const perspectiveValue = fontSize * 5;
    const transformOrigin = `50% 50% ${depth}px`;

    gsap.set(title, { perspective: perspectiveValue, transformStyle: 'preserve-3d' });
    gsap.set(split.chars, {
      z: depth,
      transformOrigin,
      rotationX: -180,
      backfaceVisibility: 'hidden',
    });

    const charCount = split.chars.length;
    const charStagger = charCount > 30 ? 0.03 : charCount > 20 ? 0.045 : 0.06;

    gsap.fromTo(
      split.chars,
      { rotationX: -180 },
      {
        rotationX: 0,
        duration: 1.2,
        stagger: charStagger,
        ease: 'back.out(1.4)',
        transformOrigin,
        immediateRender: true,
        scrollTrigger: {
          trigger: trigger || title,
          start: 'top 80%',
          once: true,
        },
      }
    );
  }

  applyBlurReveal(
    document.querySelector('.feature.feature--inbetweening .feature__title'),
    { trigger: '.feature.feature--inbetweening' }
  );

  applyBlurReveal(
    document.querySelector('.main-art .main-art__title'),
    { trigger: '.main-art' }
  );

  applyBlurReveal(
    document.querySelector('.main-art-2 .main-art-2__title'),
    { trigger: '.main-art-2' }
  );

  {
    const programsTitle = document.querySelector('.programs .programs__title');

    if (programsTitle) {
      applyBlurReveal(programsTitle);
    }
  }

  {
    const storiesTitle = document.querySelector('.stories .stories__title');

    if (storiesTitle) {
      applyBlurReveal(storiesTitle);
    }
  }

  function applyBlurReveal(el, opts = {}) {
    if (!el) {
      return;
    }

    const split = new SplitText(el, { type: 'words', wordsClass: 'blur-word' });
    const computed = window.getComputedStyle(el);
    const fontSize = parseFloat(computed.fontSize);
    const blurRadius = Math.max(30, fontSize * 1.5);
    const wordColorList = split.words.map((word) => opts.color || window.getComputedStyle(word).color);

    gsap.set(el, { opacity: 1 });
    gsap.set(split.words, { color: 'transparent', display: 'inline-block' });

    const scrollTriggerVars = {
      trigger: opts.trigger || el,
      start: opts.start || 'top 80%',
    };

    if (opts.isRepeated) {
      scrollTriggerVars.toggleActions = 'restart none restart reset';
    } else {
      scrollTriggerVars.once = true;
    }

    gsap.fromTo(
      split.words,
      { textShadow: (index) => `0 0 ${blurRadius}px ${wordColorList[index]}`, opacity: 0 },
      {
        textShadow: (index) => `0 0 0px ${wordColorList[index]}`,
        opacity: 1,
        duration: opts.duration || 0.7,
        stagger: opts.stagger || 0.05,
        ease: 'power2.out',
        immediateRender: true,
        scrollTrigger: scrollTriggerVars,
      }
    );
  }

  document.querySelectorAll('.hero .hero__title-layer').forEach((layer) => {
    gsap.set(layer, { opacity: 1 });
  });

  applyBlurReveal(
    document.querySelector('.hero .hero__subtitle'),
    { trigger: '.hero' }
  );

  applyBlurReveal(
    document.querySelector('.feature.feature--inbetweening .feature__description'),
    { trigger: '.feature.feature--inbetweening' }
  );

  applyBlurReveal(
    document.querySelector('.main-art .main-art__description'),
    { trigger: '.main-art' }
  );

  applyBlurReveal(
    document.querySelector('.main-art-2 .main-art-2__description'),
    { trigger: '.main-art-2' }
  );

  {
    const discordTitle = document.querySelector('.discord .discord__title');

    if (discordTitle) {
      applyBlurReveal(discordTitle);
    }
  }

  {
    const programsDesc = document.querySelector('.programs .programs__description');

    if (programsDesc) {
      programsDesc.querySelectorAll('strong').forEach((strong) => {
        const span = document.createElement('span');
        span.style.fontWeight = '700';
        while (strong.firstChild) span.appendChild(strong.firstChild);
        strong.replaceWith(span);
      });
      applyBlurReveal(programsDesc);
    }
  }

  ['.pose', '.pose-2', '.pose-3', '.pose-4', '.pose-5', '.pose-6'].forEach((selector) => {
    const section = document.querySelector(selector);

    if (!section) {
      return;
    }

    const title = section.querySelector('h3');
    const description = section.querySelector('p');

    applyBlurReveal(title, { trigger: section });
    applyBlurReveal(description, { trigger: section });
  });

  const mm = gsap.matchMedia();

  mm.add('(orientation: landscape)', () => {
    const firstPinSection = document.querySelector('.feature.feature--inbetweening');

    if (firstPinSection) {
      const firstPinDesignDistance = 900;
      const firstHeaderDesignDistance = 630;
      const firstFadeDesignShift = 300;
      const firstFadeDesignEnd =
        (firstPinDesignDistance + firstFadeDesignShift) * FADE_END_FACTOR;
      const firstFadeStartRatio = firstFadeDesignShift / firstFadeDesignEnd;

      ScrollTrigger.create({
        trigger: firstPinSection,
        start: 'top top',
        end: () => `+=${scrollDistance(firstPinDesignDistance)}`,
        pin: true,
        pinSpacing: false,
        anticipatePin: 1,
      });

      const firstVisual = firstPinSection.querySelector('.feature__visual');

      if (firstVisual) {
        const firstVisualTimeline = gsap.timeline({
          scrollTrigger: {
            trigger: firstPinSection,
            start: 'top top',
            end: () => `+=${scrollDistance(firstFadeDesignEnd)}`,
            scrub: 0.5,
          },
        });

        firstVisualTimeline.fromTo(
          firstVisual,
          { autoAlpha: 1 },
          { autoAlpha: 0, ease: 'none', duration: 1 - firstFadeStartRatio },
          firstFadeStartRatio
        );
      }

      const firstHeader = firstPinSection.querySelector('.feature__content');

      if (firstHeader) {
        gsap.fromTo(
          firstHeader,
          { y: 0, autoAlpha: 1 },
          {
            y: 80,
            autoAlpha: 0,
            ease: 'none',
            scrollTrigger: {
              trigger: firstPinSection,
              start: 'top top',
              end: () => `+=${scrollDistance(firstHeaderDesignDistance)}`,
              scrub: 0.5,
            },
          }
        );
      }
    }

    const pinSection = document.querySelector('.main-art');

    if (pinSection) {
      const pinDesignDistance = 900;
      const fadeDesignShift = 300;
      const fadeDesignEnd = (pinDesignDistance + fadeDesignShift) * FADE_END_FACTOR;
      const fadeStartRatio = fadeDesignShift / fadeDesignEnd;

      ScrollTrigger.create({
        trigger: pinSection,
        start: 'top top',
        end: () => `+=${scrollDistance(pinDesignDistance)}`,
        pin: true,
        pinSpacing: false,
        anticipatePin: 1,
      });

      const fadeTargetList = pinSection.querySelectorAll(
        '.main-art__video, .main-art__visual, .main-art__header'
      );

      if (fadeTargetList.length) {
        const visualTimeline = gsap.timeline({
          scrollTrigger: {
            trigger: pinSection,
            start: 'top top',
            end: () => `+=${scrollDistance(fadeDesignEnd)}`,
            scrub: 0.5,
          },
        });

        visualTimeline.fromTo(
          fadeTargetList,
          { filter: 'brightness(1)' },
          { filter: 'brightness(0)', ease: 'none', duration: 1 - fadeStartRatio },
          fadeStartRatio
        );
      }

    }
  });

  const postsItems = document.querySelectorAll('.posts__slide.is-active .posts__item');

  if (postsItems.length) {
    gsap.from(postsItems, {
      y: 40,
      autoAlpha: 0,
      duration: 0.6,
      stagger: 0.12,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: '.posts',
        start: 'top 75%',
        once: true,
      },
    });
  }

  applyBlurReveal(document.querySelector('.intro__heading'), { trigger: '.intro' });

  const trusted = document.querySelector('.trusted');

  if (trusted) {
    const tlTrusted = gsap.timeline({
      scrollTrigger: {
        trigger: trusted,
        start: 'top 80%',
        once: true,
      },
    });
    tlTrusted
      .from('.trusted__caption', {
        y: 40,
        autoAlpha: 0,
        duration: 0.6,
        ease: 'power2.out',
      })
      .from(
        '.trusted__logo',
        {
          y: 40,
          autoAlpha: 0,
          duration: 0.6,
          stagger: 0.08,
          ease: 'power2.out',
        },
        '-=0.3'
      );
  }

  const programs = document.querySelector('.programs__inner');

  if (programs) {
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: programs,
        start: 'top 80%',
        once: true,
      },
    });
    tl.from('.programs__header', {
      y: 50,
      autoAlpha: 0,
      duration: 0.7,
      ease: 'power2.out',
    }).from(
      '.programs__logo',
      {
        y: 40,
        autoAlpha: 0,
        duration: 0.6,
        stagger: 0.08,
        ease: 'power2.out',
      },
      '-=0.3'
    );
  }

  const discord = document.querySelector('.discord');

  if (discord) {
    const tlDiscord = gsap.timeline({
      scrollTrigger: {
        trigger: discord,
        start: 'top 80%',
        once: true,
      },
    });
    tlDiscord
      .from('.discord__logo', {
        y: 40,
        autoAlpha: 0,
        duration: 0.6,
        ease: 'power2.out',
      })
      .from(
        '.discord__btn',
        {
          y: 30,
          autoAlpha: 0,
          duration: 0.5,
          ease: 'power2.out',
        },
        '-=0.3'
      );
  }

  const community = document.querySelector('.community');

  if (community) {
    const tlCommunity = gsap.timeline({
      scrollTrigger: {
        trigger: community,
        start: 'top 80%',
        once: true,
      },
    });
    gsap.utils.toArray('.community__column').forEach((column, index) => {
      tlCommunity.from(
        column.children,
        {
          autoAlpha: 0,
          duration: 0.7,
          stagger: 0.08,
          ease: 'power2.out',
          immediateRender: true,
        },
        index === 0 ? '-=0.3' : `<+=0.05`
      );
    });
  }

  ScrollTrigger.refresh();
}
